import Link from 'next/link';
import React, { FormEvent, useState } from 'react';
import DropdownComponent from '../util/Form/DropdownComponent';
import InputComponent, { BorderColors, Invalid } from '../util/Form/InputComponent';
import TextAreaComponent from '../util/Form/TextAreaComponent';
import styles from './contact.module.scss';
import LargeWidthContainer from '../Layout/Containers/LargeWidthContainer';
import { ContactFormData, ContactProps, WhoOptions } from './ContactModels';

const InsuranceContactComponent = ({ extraStyles }: ContactProps) => {
    const [firstName, setFirstName] = useState('');
    const [firstNameInvalidBorder, toggleFirstNameInvalidBorder] = useState<BorderColors>(null);
    const [invalidFirstName, toggleInvalidFirstName] = useState<Invalid>('none');

    const [lastName, setLastName] = useState('');
    const [lastNameInvalidBorder, toggleLastNameInvalidBorder] = useState<BorderColors>(null);
    const [invalidLastName, toggleInvalidLastName] = useState<Invalid>('none');

    const [email, setEmail] = useState('');
    const [emailInvalidBorder, toggleEmailInvalidBorder] = useState<BorderColors>(null);
    const [invalidEmail, toggleInvalidEmail] = useState<Invalid>('none');

    const [who, setWho] = useState<WhoOptions>(WhoOptions.NONE);
    const [whoInvalidBorder, toggleWhoInvalidBorder] = useState<BorderColors>(null);
    const [invalidWho, toggleInvalidWho] = useState<Invalid>('none');

    const [message, setMessage] = useState('');
    const [messageInvalidBorder, toggleMessageInvalidBorder] = useState('#c3c3c5');
    const [invalidMessage, toggleInvalidMessage] = useState<Invalid>('none');

    const [messageSentSuccessfully, setMessageSentSuccessfully] = useState(false);
    const [messageSentError, setMessageSentError] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    const sentContactEmail = (formData: ContactFormData) => {
        return fetch(`${process.env.NEXT_PUBLIC_API_URL}/send-email`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify(formData),
        });
    };

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (email.length > 0) {
            toggleInvalidEmail('none');
        } else {
            toggleEmailInvalidBorder('var(--primary-red)');
            toggleInvalidEmail('block');
        }
        if (firstName.length > 0) {
            toggleInvalidFirstName('none');
        } else {
            toggleFirstNameInvalidBorder('var(--primary-red)');
            toggleInvalidFirstName('block');
        }
        if (lastName.length > 0) {
            toggleInvalidLastName('none');
        } else {
            toggleLastNameInvalidBorder('var(--primary-red)');
            toggleInvalidLastName('block');
        }
        if (message.length > 0) {
            toggleInvalidMessage('none');
        } else {
            toggleMessageInvalidBorder('var(--primary-red)');
            toggleInvalidMessage('block');
        }
        if (who.length > 0) {
            toggleInvalidWho('none');
        } else {
            toggleWhoInvalidBorder('var(--primary-red)');
            toggleInvalidWho('block');
        }

        if (
            email.length > 0 &&
            firstName.length > 0 &&
            lastName.length > 0 &&
            message.length > 0 &&
            who.length > 0
        ) {
            const formData: ContactFormData = {
                firstName,
                lastName,
                email,
                message,
                who,
            };

            setSubmitting(true);
            setMessageSentSuccessfully(false);

            sentContactEmail(formData)
                .then((response) => {
                    setSubmitting(false);
                    if (response.status == 200) {
                        setMessageSentSuccessfully(true);
                        setMessageSentError(false);
                    } else {
                        setMessageSentSuccessfully(false);
                        setMessageSentError(true);
                    }
                })
                .catch((error) => {
                    setMessageSentSuccessfully(false);
                    setMessageSentError(true);
                    setSubmitting(false);
                });
        }
    };

    return (
        <section style={extraStyles}>
            <LargeWidthContainer extraClassNames={styles.contactContainer}>
                <div className={styles.doubleColumn}>
                    <div className={styles.child}>
                        <h1 className={styles.ingress}>
                            Where Your Insurance
                            <br /> Software Story Begins
                        </h1>
                        <p className={styles.description}>
                            Are you ready to write the next chapter of your success story?
                        </p>
                    </div>
                    <div className={styles.child}>
                        <form className={styles.form} onSubmit={onSubmit} name="contact">
                            <div className={styles.nameContainer}>
                                <InputComponent
                                    id="firstName"
                                    label="First name"
                                    placeholder="First name"
                                    type="text"
                                    value={firstName}
                                    setValue={setFirstName}
                                    toggleInvalid={toggleInvalidFirstName}
                                    invalid={invalidFirstName}
                                    invalidBorder={firstNameInvalidBorder}
                                    toggleBorderColor={toggleFirstNameInvalidBorder}
                                />
                                <InputComponent
                                    id="lastName"
                                    label="Last name"
                                    placeholder="Last name"
                                    type="text"
                                    value={lastName}
                                    setValue={setLastName}
                                    toggleInvalid={toggleInvalidLastName}
                                    invalid={invalidLastName}
                                    invalidBorder={lastNameInvalidBorder}
                                    toggleBorderColor={toggleLastNameInvalidBorder}
                                />
                            </div>
                            <div style={{ width: '100%' }}>
                                <InputComponent
                                    id="email"
                                    label="Your email"
                                    placeholder="Your email"
                                    type="email"
                                    value={email}
                                    setValue={setEmail}
                                    invalid={invalidEmail}
                                    toggleInvalid={toggleInvalidEmail}
                                    invalidBorder={emailInvalidBorder}
                                    toggleBorderColor={toggleEmailInvalidBorder}
                                />
                            </div>
                            <DropdownComponent
                                id="dropdown"
                                label="How can we help you*"
                                selectName="How can we help you"
                                onChange={() => {}}
                                placeholder="How can we help you*"
                                value={who}
                                setValue={setWho}
                                options={[
                                    {
                                        label: 'How can we help you*',
                                        value: WhoOptions.NONE,
                                    },
                                    {
                                        label: 'Marine & Energy',
                                        value: WhoOptions.MARINE_ENERGY,
                                    },
                                    {
                                        label: 'Captive Insurance',
                                        value: WhoOptions.CAPTIVE,
                                    },
                                    {
                                        label: 'Property & Casualty',
                                        value: WhoOptions.PROPERTY_CASUALTY,
                                    },
                                ]}
                                invalid={invalidWho}
                                toggleInvalid={toggleInvalidWho}
                                borderColor={whoInvalidBorder}
                                toggleBorderColor={toggleWhoInvalidBorder}
                            />
                            <div style={{ width: '100%' }}>
                                <TextAreaComponent
                                    id="message"
                                    label="Message*"
                                    form="contact"
                                    value={message}
                                    setValue={setMessage}
                                    invalid={invalidMessage}
                                    toggleInvalid={toggleInvalidMessage}
                                    borderColor={messageInvalidBorder}
                                    toggleBorderColor={toggleMessageInvalidBorder}
                                />
                            </div>
                            <p className={styles.information}>
                                Noria requires your information to contact you regarding our
                                solutions and services. For details on our privacy practices and our
                                commitment to protecting your privacy, please review our Privacy
                                Policy. &nbsp;
                                <Link
                                    href={`/privacy`}
                                    title="Go to the Privacy and Cookie policy page"
                                >
                                    <span>Privacy Policy</span>
                                </Link>
                                .
                            </p>
                            <div>
                                <button type="submit" className="button-link button-large">
                                    Submit
                                </button>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        fontSize: '14px',
                                        marginTop: '10px',
                                    }}
                                >
                                    {messageSentError ? (
                                        <div style={{ color: 'var(--primary-red)' }}>
                                            We can't reach out to you. Please try again!
                                        </div>
                                    ) : null}

                                    {messageSentSuccessfully ? (
                                        <div style={{ color: 'var(--primary-green' }}>
                                            Thank you for your inquiry. We will get back to you as
                                            soon as possible.
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </LargeWidthContainer>
        </section>
    );
};
export default InsuranceContactComponent;
